import { GenerationLoader } from '@wix/cc-user-facing-common-components';
import { IconButton, Text, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Chat as ChatIcon, LayoutThird } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CHATBOT_IFRAME_URL } from '../../../constants';
import { useStore } from '../../../zustand/stores';
import { CopilotDisplayMode } from '../../../zustand/stores/copilot';
import {
  createIframeMessageHandler,
  initChatbotParams,
} from '../../ArticleRightSideBar/iframeMessageHandler';

import css from './index.module.scss';

type ArticleRightPanelProps = {
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
  isCollapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  suggestedQuestions?: string[];
};

const noop = () => undefined;

export const ArticleRightPanel = ({
  selfArticleRedirect,
  isCollapsed,
  onCollapse,
  onExpand,
  suggestedQuestions,
}: ArticleRightPanelProps) => {
  const {
    copilotDisplayMode,
    isChatbotReady,
    setIsChatbotReady,
    setChatbotFlow,
    setUnreadMessagesCount,
    chatbotReferral,
    chatbotNodeId,
  } = useStore(state => state.copilotSlice);
  const { selectedSite } = useStore(state => state.siteSelectorSlice);
  const { t } = useTranslation();
  const { locale } = useRouter();

  const isLoading = copilotDisplayMode === CopilotDisplayMode.Loading;
  const isActive =
    copilotDisplayMode === CopilotDisplayMode.Chatbot || isLoading;

  const prevCollapsedRef = useRef(isCollapsed);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  if (prevCollapsedRef.current !== isCollapsed) {
    prevCollapsedRef.current = isCollapsed;
    if (!shouldAnimate) {
      setShouldAnimate(true);
    }
  }

  useEffect(() => {
    if (isLoading && isCollapsed) {
      onExpand();
    }
  }, [isLoading, isCollapsed, onExpand]);

  const handleIframeMessage = useMemo(
    () =>
      createIframeMessageHandler({
        goBack: noop,
        setShowRightSideBar: () => onCollapse(),
        setIsChatbotReady,
        selfArticleRedirect,
        setChatbotFlow,
        setUnreadMessagesCount,
        chatbotReferral,
        chatbotNodeId,
        selectedSiteMetaSiteId: selectedSite?.metaSiteId,
        suggestedQuestions,
      }),
    [
      onCollapse,
      setIsChatbotReady,
      selfArticleRedirect,
      setChatbotFlow,
      setUnreadMessagesCount,
      chatbotReferral,
      chatbotNodeId,
      selectedSite,
      suggestedQuestions,
    ],
  );

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [handleIframeMessage]);

  useEffect(() => {
    if (isChatbotReady) {
      initChatbotParams(
        window?.location.href,
        chatbotReferral ?? undefined,
        chatbotNodeId ?? undefined,
        selectedSite?.metaSiteId,
        suggestedQuestions,
      );
    }
  }, [suggestedQuestions, isChatbotReady, chatbotReferral, chatbotNodeId, selectedSite]);

  return (
    <div
      className={classNames(css.wrapper, {
        [css.inactive]: !isActive,
        [css.collapsing]: isActive && shouldAnimate && isCollapsed,
        [css.expanding]: isActive && shouldAnimate && !isCollapsed,
        [css.collapsed]: isActive && !shouldAnimate && isCollapsed,
      })}
    >
      {isActive && (
        <>
          <div
            className={classNames(css.iframeContainer, {
              [css.hidden]: isCollapsed || isLoading,
            })}
          >
            <iframe
              src={CHATBOT_IFRAME_URL}
              className={css.chatbotIframe}
              id="chatbot-iframe"
            />
          </div>
          {isLoading && !isCollapsed && (
            <div className={css.loadingPanel}>
              <div className={css.loadingHeader}>
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  disabled
                >
                  <LayoutThird />
                </IconButton>
              </div>
              <div className={css.loadingContent}>
                <GenerationLoader
                  locale={locale as string}
                  className={css.loadingIcon}
                />
                <Text weight="thin" size="tiny">
                  {t('article-page.copilot.loader.txt')}
                </Text>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className={css.collapsedPanel}>
              <Tooltip content={t('article-page.right-panel.tooltip')} placement="top">
                <IconButton
                  size="small"
                  skin="dark"
                  priority="secondary"
                  onClick={onExpand}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </div>
  );
};
